import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

export default function Features() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const features = [
    {
      title: 'Virtualization & Infrastructure',
      description: 'Build robust, scalable infrastructure with open-source virtualization platforms. Expert design and implementation of Proxmox-based solutions for enterprise environments.',
      image: 'https://public.readdy.ai/ai/img_res/9ffed4e9c4c798b1534a1abf118f6069.jpg',
      icon: 'ri-server-line',
      stats: 'Proxmox Expert',
      color: 'from-teal-500 to-cyan-500',
    },
    {
      title: 'Backup & Storage Design',
      description: 'Comprehensive backup infrastructure and storage consultation. Implement reliable TrueNAS solutions with disaster recovery planning and data protection strategies.',
      image: 'https://readdy.ai/api/search-image?query=enterprise%20data%20storage%20backup%20infrastructure%20with%20glowing%20teal%20cyan%20accents%20abstract%20technology%20concept%20floating%20storage%20arrays%20clean%20white%20environment%20professional%20atmosphere%20modern%20digital%20design&width=600&height=500&seq=kn-feat2-storage&orientation=landscape',
      icon: 'ri-database-2-line',
      stats: 'TrueNAS Certified',
      color: 'from-cyan-500 to-teal-500',
    },
    {
      title: 'Network Security & Monitoring',
      description: 'Advanced network security solutions with real-time monitoring. Deploy enterprise-grade firewalls and security systems from Palo Alto Networks, Fortigate, and Unifi.',
      image: 'https://readdy.ai/api/search-image?query=network%20security%20monitoring%20dashboard%20with%20teal%20cyan%20glowing%20data%20streams%20abstract%20cybersecurity%20visualization%20floating%20shield%20icons%20clean%20minimalist%20background%20professional%20enterprise%20atmosphere%20digital%20protection%20concept&width=600&height=500&seq=kn-feat3-security&orientation=landscape',
      icon: 'ri-shield-check-line',
      stats: 'Multi-Vendor',
      color: 'from-teal-500 to-emerald-500',
    },
    {
      title: 'Systems Design & Support',
      description: 'End-to-end systems design consultation and ongoing support. Expert guidance on ElasticSearch implementation, infrastructure optimization, and technical architecture.',
      image: 'https://readdy.ai/api/search-image?query=systems%20architecture%20design%20blueprint%20with%20teal%20cyan%20holographic%20interface%20elements%20abstract%20technology%20planning%20concept%20floating%20diagrams%20clean%20white%20background%20professional%20enterprise%20atmosphere%20modern%20digital%20visualization&width=600&height=500&seq=kn-feat4-design&orientation=landscape',
      icon: 'ri-settings-3-line',
      stats: 'Full Support',
      color: 'from-cyan-500 to-sky-500',
    },
  ];

  return (
    <section id="features" className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden" ref={ref}>
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-teal-200/20 to-cyan-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-tr from-cyan-200/20 to-teal-200/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-6 shadow-sm">
            <i className="ri-service-line text-teal-600"></i>
            <span className="text-sm font-medium text-gray-700">Our Services</span>
          </div>
          <h2 className="text-5xl lg:text-6xl font-display font-bold text-gray-900 leading-tight mb-6">
            Comprehensive IT Solutions
            <br />
            <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
              Built on Open Source
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Expert infrastructure services leveraging the power of open-source technology and industry-leading vendor solutions.
          </p>
        </motion.div>

        {/* Features List */}
        <div className="space-y-24">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: index * 0.2 }}
              className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12 lg:gap-16`}
            >
              {/* Image Side */}
              <div className="w-full lg:w-1/2">
                <div className="relative group">
                  {/* Gradient Border Effect */}
                  <div className={`absolute -inset-1 bg-gradient-to-r ${feature.color} rounded-3xl blur-lg opacity-25 group-hover:opacity-40 transition-opacity duration-500`}></div>
                  
                  {/* Image Container */}
                  <div className="relative bg-white rounded-2xl overflow-hidden shadow-xl">
                    <div className="relative w-full h-96">
                      <img
                        src={feature.image}
                        alt={feature.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent"></div>
                      
                      {/* Stats Badge */}
                      <div className="absolute bottom-6 left-6 px-5 py-3 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${feature.color}`}></div>
                          <span className="text-sm font-bold text-gray-900">{feature.stats}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Side */}
              <div className="w-full lg:w-1/2">
                <div className="space-y-6">
                  {/* Icon Badge */}
                  <div className={`inline-flex w-16 h-16 items-center justify-center bg-gradient-to-br ${feature.color} rounded-2xl shadow-lg`}>
                    <i className={`${feature.icon} text-3xl text-white`}></i>
                  </div>

                  {/* Title */}
                  <h3 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>

                  {/* Feature Points */}
                  <div className="space-y-3 pt-4">
                    {index === 0 && [
                      'Proxmox virtualization platform',
                      'High availability clustering',
                      'Resource optimization & scaling',
                    ].map((point, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <div className={`w-6 h-6 flex items-center justify-center bg-gradient-to-br ${feature.color} rounded-full flex-shrink-0`}>
                          <i className="ri-check-line text-sm text-white"></i>
                        </div>
                        <span className="text-base text-gray-700">{point}</span>
                      </div>
                    ))}
                    {index === 1 && [
                      'TrueNAS storage solutions',
                      'Disaster recovery planning',
                      'Data protection strategies',
                    ].map((point, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <div className={`w-6 h-6 flex items-center justify-center bg-gradient-to-br ${feature.color} rounded-full flex-shrink-0`}>
                          <i className="ri-check-line text-sm text-white"></i>
                        </div>
                        <span className="text-base text-gray-700">{point}</span>
                      </div>
                    ))}
                    {index === 2 && [
                      'Palo Alto Networks & Fortigate',
                      'Unifi network infrastructure',
                      'Real-time threat monitoring',
                    ].map((point, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <div className={`w-6 h-6 flex items-center justify-center bg-gradient-to-br ${feature.color} rounded-full flex-shrink-0`}>
                          <i className="ri-check-line text-sm text-white"></i>
                        </div>
                        <span className="text-base text-gray-700">{point}</span>
                      </div>
                    ))}
                    {index === 3 && [
                      'ElasticSearch implementation',
                      'Infrastructure consultation',
                      'Ongoing technical support',
                    ].map((point, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <div className={`w-6 h-6 flex items-center justify-center bg-gradient-to-br ${feature.color} rounded-full flex-shrink-0`}>
                          <i className="ri-check-line text-sm text-white"></i>
                        </div>
                        <span className="text-base text-gray-700">{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
